import { getUserVisibleOrDefaultExceptionMessage } from '@paragon/client-tools';
import { ScheduleGroup } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { getLoggedInUser } from '@paragon/login-tools';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';
import { notNullish } from '@paragon/utilities';

import { bundlesLoaded } from '../store/bundlesSlice';
import { scheduleGroupCreated, scheduleGroupDeleted, scheduleGroupsLoaded } from '../store/scheduleGroupsSlice';
import { AsyncThunkActionVoid } from './thunkActions';

export function createScheduleGroup(
  bundleGuids: readonly string[],
  name: string,
  targetDate: string | null,
): AsyncThunkActionVoid {
  return async function createScheduleGroupThunkAction(dispatch, getState) {
    const { bundles } = getState();
    const bundlesAlreadyAssignedToAScheduleGroup = bundleGuids
      .map((bundleGuid) => bundles[bundleGuid].scheduleGroupGuid)
      .filter(notNullish);
    if (bundlesAlreadyAssignedToAScheduleGroup.length > 0)
      throw new Error('Bundles are already assigned to a schedule group');

    const spinnerId = dispatch(addSpinnerMessage('Creating schedule group...'));

    try {
      const loggedInUser = getLoggedInUser(getState());
      const scheduleGroup: ScheduleGroup = {
        guid: crypto.randomUUID(),
        name,
        organization: loggedInUser!.organization,
        targetDate,
      };
      await designService.scheduleGroups.insert(scheduleGroup);
      dispatch(scheduleGroupCreated(scheduleGroup));
      await dispatch(addToScheduleGroup(scheduleGroup.guid, bundleGuids));
    } catch {
      dispatch(enqueueError('Error creating schedule group'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function addToScheduleGroup(
  scheduleGroupGuid: string,
  bundleGuids: readonly string[],
  spinner = true,
): AsyncThunkActionVoid {
  return async function addToScheduleGroupThunkAction(dispatch, getState) {
    const { bundles } = getState();
    const bundlesAlreadyAssignedToAScheduleGroup = bundleGuids
      .map((bundleGuid) => bundles[bundleGuid].scheduleGroupGuid)
      .filter(notNullish);
    if (bundlesAlreadyAssignedToAScheduleGroup.length > 0)
      throw new Error('Bundles are already assigned to a schedule group');

    const spinnerId = spinner ? dispatch(addSpinnerMessage('Adding to schedule group...')) : null;

    try {
      const updatedBundles = await designService.bundles.assignToScheduleGroup(bundleGuids, scheduleGroupGuid);
      dispatch(bundlesLoaded(updatedBundles));
    } catch (exception) {
      dispatch(
        enqueueError(getUserVisibleOrDefaultExceptionMessage(exception, 'Error adding bundles to schedule group')),
      );
    } finally {
      if (spinnerId != null) dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function editScheduleGroup(guid: string, name: string, targetDate: string | null): AsyncThunkActionVoid {
  return async function editScheduleGroupThunkAction(dispatch, getState) {
    const spinnerId = dispatch(addSpinnerMessage('Editing schedule group...'));

    try {
      const existingScheduleGroup = getState().scheduleGroups[guid];
      const updatedScheduleLineGroup = {
        ...existingScheduleGroup,
        name,
        targetDate,
      };

      await designService.scheduleGroups.update(updatedScheduleLineGroup);
      dispatch(scheduleGroupsLoaded([updatedScheduleLineGroup]));
    } catch {
      dispatch(enqueueError('Error editing schedule group'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function removeBundleFromScheduleGroup(bundleGuid: string): AsyncThunkActionVoid {
  return async function removeBundleFromScheduleGroupThunkAction(dispatch) {
    const spinnerId = dispatch(addSpinnerMessage('Removing bundle from schedule group...'));

    try {
      const updatedBundle = await designService.bundles.removeFromScheduleGroup(bundleGuid);
      dispatch(bundlesLoaded([updatedBundle]));
    } catch (exception) {
      dispatch(
        enqueueError(getUserVisibleOrDefaultExceptionMessage(exception, 'Error removing bundle from schedule group')),
      );
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function deleteScheduleGroup(scheduleGroupGuid: string): AsyncThunkActionVoid {
  return async function removeScheduleGroupThunkAction(dispatch) {
    const spinnerId = dispatch(addSpinnerMessage('Removing schedule group...'));

    try {
      const updatedBundles = await designService.scheduleGroups.remove(scheduleGroupGuid);
      dispatch(scheduleGroupDeleted({ scheduleGroupGuid, updatedBundles }));
    } catch (exception) {
      dispatch(enqueueError(getUserVisibleOrDefaultExceptionMessage(exception, 'Error removing schedule group')));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}
